import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SlidersHorizontal, List, Grid2X2, Grid3X3, LayoutGrid, X, ChevronDown } from 'lucide-react';
import categoryService from '../../services/categoryService';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import ProductCard from '../../components/ProductCard/ProductCard';

const CategoryPage = () => {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid-4'); // grid-2, grid-3, grid-4, list
  const [sortBy, setSortBy] = useState('best-selling');

  // Filter drawer state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(true);
  const [isAvailabilityOpen, setIsAvailabilityOpen] = useState(true);
  const [isPriceOpen, setIsPriceOpen] = useState(true);

  // Filter values
  const [availabilityFilter, setAvailabilityFilter] = useState({ inStock: false, outOfStock: false });
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100000 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoryResponse, categoriesResponse] = await Promise.all([
          categoryService.getCategoryProducts(categoryId),
          categoryService.getCategories()
        ]);

        if (categoryResponse.success) {
          setCategory(categoryResponse.category);
          setProducts(categoryResponse.products || categoryResponse.category.products || []);
        } else {
          setError('Category not found');
        }

        if (categoriesResponse.success) {
          setAllCategories(categoriesResponse.categories);
        }
      } catch (err) {
        setError('Error loading category');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId]);

  const getGridClasses = () => {
    switch (viewMode) {
      case 'list':
        return 'grid grid-cols-1 gap-8';
      case 'grid-2':
        return 'grid grid-cols-1 md:grid-cols-2 gap-8';
      case 'grid-3':
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8';
      case 'grid-4':
        return 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8';
      case 'grid-5':
        return 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6';
      case 'grid-6':
        return 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6';
      default:
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8';
    }
  };

  // Filter products
  const getFilteredProducts = () => {
    let filtered = [...products];

    // Apply availability filter
    if (availabilityFilter.inStock || availabilityFilter.outOfStock) {
      filtered = filtered.filter(product => {
        const inStock = product.inventory && product.inventory.some(inv => parseInt(inv) > 0);
        if (availabilityFilter.inStock && availabilityFilter.outOfStock) return true;
        if (availabilityFilter.inStock) return inStock;
        if (availabilityFilter.outOfStock) return !inStock;
        return true;
      });
    }

    // Apply price filter
    filtered = filtered.filter(product => {
      const price = parseFloat(product.msrp) || 0;
      return price >= priceRange.min && price <= priceRange.max;
    });

    return filtered;
  };

  const sortedProducts = getFilteredProducts().sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return (parseFloat(a.msrp) || 0) - (parseFloat(b.msrp) || 0);
      case 'price-high':
        return (parseFloat(b.msrp) || 0) - (parseFloat(a.msrp) || 0);
      case 'newest':
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      default:
        return 0; // Best selling - no sort
    }
  });

  // Count products by availability
  const inStockCount = products.filter(p => p.inventory && p.inventory.some(inv => parseInt(inv) > 0)).length;
  const outOfStockCount = products.length - inStockCount;

  // Skeleton loader component - matches ProductCard exactly
  const ProductCardSkeleton = () => (
    <div className="group animate-pulse">
      <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4">
        <div className="w-full h-[400px] object-cover bg-gray-200"></div>
      </div>
      <h3 className="text-base mb-2 text-gray-900 bg-gray-200 rounded h-5 w-full">
        <span className="sr-only">Loading product</span>
      </h3>
      <p className="text-sm text-gray-600 mb-3 bg-gray-200 rounded h-4 w-3/4">
        <span className="sr-only">Loading price</span>
      </p>
      <div className="flex gap-2 flex-wrap">
        <div className="w-6 h-6 rounded-full border-2 border-transparent bg-gray-200"></div>
        <div className="w-6 h-6 rounded-full border-2 border-transparent bg-gray-200"></div>
        <div className="w-6 h-6 rounded-full border-2 border-transparent bg-gray-200"></div>
        <div className="w-6 h-6 rounded-full border-2 border-transparent bg-gray-200"></div>
        <div className="w-6 h-6 rounded-full border-2 border-transparent bg-gray-200"></div>
        <div className="w-6 h-6 rounded-full border-2 border-transparent bg-gray-200"></div>
        <div className="w-6 h-6 rounded-full border-2 border-transparent bg-gray-200"></div>
        <div className="w-6 h-6 rounded-full border-2 border-transparent bg-gray-200"></div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />

        <main className="max-w-7xl mx-auto px-8 py-12">
          {/* Category Title Skeleton */}
          <div className="text-center mb-16">
            <div className="h-10 bg-gray-200 rounded w-48 mx-auto animate-pulse"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="w-full flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
            <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-10 h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-10 h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-10 h-10 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
          </div>

          {/* Products Grid Skeleton */}
          <div className={`${getGridClasses()}`}>
            <ProductCardSkeleton />
            <ProductCardSkeleton />
            <ProductCardSkeleton />
            <ProductCardSkeleton />
            <ProductCardSkeleton />
            <ProductCardSkeleton />
            <ProductCardSkeleton />
            <ProductCardSkeleton />
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex justify-center items-center min-h-[60vh] flex-grow">
          <div className="text-red-600 text-xl">{error || 'Category not found'}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Filter Drawer Overlay */}
      {isFilterOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsFilterOpen(false)}
        />
      )}

      {/* Filter Drawer */}
      <div className={`fixed left-0 top-0 h-full w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${isFilterOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5" />
              <h2 className="text-lg font-semibold">FILTER</h2>
            </div>
            <button onClick={() => setIsFilterOpen(false)} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Product Categories */}
          <div className="mb-6 border-b border-gray-200 pb-6">
            <button
              onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
              className="flex items-center justify-between w-full text-left mb-4 hover:text-gray-600 transition-colors"
            >
              <h3 className="font-semibold">Product categories</h3>
              <div className={`transform transition-transform duration-200 ${isCategoriesOpen ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-4 h-4" />
              </div>
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${isCategoriesOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                }`}
            >
              <div className="space-y-3">
                <Link
                  to="/"
                  className="block text-sm text-gray-700 hover:text-red-500 transition-colors"
                >
                  All Products
                </Link>
                <Link
                  to="/"
                  className="block text-sm text-gray-700 hover:text-red-500 transition-colors"
                >
                  Home page
                </Link>
                {allCategories.map((cat) => (
                  <Link
                    key={cat._id}
                    to={`/category/${cat._id}`}
                    className={`block text-sm transition-colors ${cat._id === categoryId ? 'text-red-500 font-medium' : 'text-gray-700 hover:text-red-500'}`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className="mb-6 border-b border-gray-200 pb-6">
            <button
              onClick={() => setIsAvailabilityOpen(!isAvailabilityOpen)}
              className="flex items-center justify-between w-full text-left mb-4 hover:text-gray-600 transition-colors"
            >
              <h3 className="font-semibold">Availability</h3>
              <div className={`transform transition-transform duration-200 ${isAvailabilityOpen ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-4 h-4" />
              </div>
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${isAvailabilityOpen ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0'
                }`}
            >
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded transition-colors">
                  <input
                    type="checkbox"
                    checked={availabilityFilter.inStock}
                    onChange={(e) => setAvailabilityFilter({ ...availabilityFilter, inStock: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                  />
                  <span className="text-sm text-gray-700">In stock ({inStockCount})</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded transition-colors">
                  <input
                    type="checkbox"
                    checked={availabilityFilter.outOfStock}
                    onChange={(e) => setAvailabilityFilter({ ...availabilityFilter, outOfStock: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                  />
                  <span className="text-sm text-gray-700">Out of stock ({outOfStockCount})</span>
                </label>
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="mb-6">
            <button
              onClick={() => setIsPriceOpen(!isPriceOpen)}
              className="flex items-center justify-between w-full text-left mb-4 hover:text-gray-600 transition-colors"
            >
              <h3 className="font-semibold">Price</h3>
              <div className={`transform transition-transform duration-200 ${isPriceOpen ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-4 h-4" />
              </div>
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${isPriceOpen ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
                }`}
            >
              <div className="space-y-4">
                {/* Price Range Slider */}
                <div className="px-1">
                  <input
                    type="range"
                    min="0"
                    max="100000"
                    step="100"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange({ ...priceRange, max: parseInt(e.target.value) })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                  />
                </div>

                {/* Price Inputs */}
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Price:</label>
                    <div className="flex items-center gap-1 border border-gray-300 rounded px-2 py-1 focus-within:border-red-500 transition-colors">
                      <span className="text-sm">$</span>
                      <input
                        type="number"
                        value={priceRange.min}
                        onChange={(e) => setPriceRange({ ...priceRange, min: parseInt(e.target.value) || 0 })}
                        className="w-full text-sm focus:outline-none"
                        min="0"
                      />
                    </div>
                  </div>
                  <span className="text-gray-400 mt-5">-</span>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">&nbsp;</label>
                    <div className="flex items-center gap-1 border border-gray-300 rounded px-2 py-1 focus-within:border-red-500 transition-colors">
                      <span className="text-sm">$</span>
                      <input
                        type="number"
                        value={priceRange.max}
                        onChange={(e) => setPriceRange({ ...priceRange, max: parseInt(e.target.value) || 100000 })}
                        className="w-full text-sm focus:outline-none"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-8 py-12">
        <h1 className="text-4xl font-light text-center mb-16">
          {category.name || category.category}
        </h1>

        <div className="w-full flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
          {/* Filter Button - Left */}
          <div className="flex items-center">
            <button
              onClick={() => setIsFilterOpen(true)}
              className="flex items-center gap-2 text-sm font-medium hover:text-[#6B9FA8] transition-colors"
            >
              <SlidersHorizontal className="w-4 h-4" />
              FILTER
            </button>
          </div>

          {/* View Mode Controls - Center */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 hover:bg-gray-100 rounded transition-colors ${viewMode === 'list' ? 'bg-gray-100' : ''}`}
              title="List view"
            >
              <List className={`w-5 h-5 ${viewMode === 'list' ? 'text-gray-900' : 'text-gray-400'}`} />
            </button>
            <button
              onClick={() => setViewMode('grid-2')}
              className={`p-2 hover:bg-gray-100 rounded transition-colors ${viewMode === 'grid-2' ? 'bg-gray-100' : ''}`}
              title="2 columns"
            >
              <Grid2X2 className={`w-5 h-5 ${viewMode === 'grid-2' ? 'text-gray-900' : 'text-gray-400'}`} />
            </button>
            <button
              onClick={() => setViewMode('grid-3')}
              className={`p-2 hover:bg-gray-100 rounded transition-colors ${viewMode === 'grid-3' ? 'bg-gray-100' : ''}`}
              title="3 columns"
            >
              <Grid3X3 className={`w-5 h-5 ${viewMode === 'grid-3' ? 'text-gray-900' : 'text-gray-400'}`} />
            </button>
            <button
              onClick={() => setViewMode('grid-4')}
              className={`p-2 hover:bg-gray-100 rounded transition-colors ${viewMode === 'grid-4' ? 'bg-gray-100' : ''}`}
              title="4 columns"
            >
              <LayoutGrid className={`w-5 h-5 ${viewMode === 'grid-4' ? 'text-gray-900' : 'text-gray-400'}`} />
            </button>
            <button
              onClick={() => setViewMode('grid-5')}
              className={`p-2 hover:bg-gray-100 rounded transition-colors ${viewMode === 'grid-5' ? 'bg-gray-100' : ''}`}
              title="5 columns"
            >
              <svg className={`w-5 h-5 ${viewMode === 'grid-5' ? 'text-gray-900' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('grid-6')}
              className={`p-2 hover:bg-gray-100 rounded transition-colors ${viewMode === 'grid-6' ? 'bg-gray-100' : ''}`}
              title="6 columns"
            >
              <svg className={`w-5 h-5 ${viewMode === 'grid-6' ? 'text-gray-900' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="4" height="4" rx="1" />
                <rect x="9" y="3" width="4" height="4" rx="1" />
                <rect x="15" y="3" width="4" height="4" rx="1" />
                <rect x="3" y="9" width="4" height="4" rx="1" />
                <rect x="9" y="9" width="4" height="4" rx="1" />
                <rect x="15" y="9" width="4" height="4" rx="1" />
                <rect x="3" y="15" width="4" height="4" rx="1" />
                <rect x="9" y="15" width="4" height="4" rx="1" />
                <rect x="15" y="15" width="4" height="4" rx="1" />
              </svg>
            </button>
          </div>

          {/* Sort Dropdown - Right */}
          <div className="flex items-center">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-[#6B9FA8]"
            >
              <option value="best-selling">Best selling</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>

        {sortedProducts.length === 0 ? (
          <p className="text-gray-500 text-center py-12">No products in this category.</p>
        ) : (
          <div
            key={viewMode}
            className={`${getGridClasses()} transition-all duration-300 ease-in-out`}
          >
            {sortedProducts.map((product, index) => (
              <div
                key={`${product._id}-${viewMode}`}
                className="animate-fadeIn"
                style={{
                  animationDelay: `${index * 80}ms`,
                  animationFillMode: 'both'
                }}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CategoryPage;
